// ============================================================================
// TAX CORE endpoints (ADR-0019).
// ============================================================================

import { buildEndpoint, buildEndpointWithParams } from './base.endpoints';
import type { ID } from '../types';

const TAX_BASE = '/tax';
export const taxMetaEndpoints = {
  jurisdictions: () => buildEndpoint(`${TAX_BASE}/jurisdictions`),
  jurisdiction: () => buildEndpoint(`${TAX_BASE}/jurisdiction`),
  fieldContext: () => buildEndpoint(`${TAX_BASE}/field-context`),
  supplyTypes: () => buildEndpoint(`${TAX_BASE}/supply-types`),
  rates: () => buildEndpoint(`${TAX_BASE}/rates`),
  components: () => buildEndpoint(`${TAX_BASE}/components`),
  inputTaxRules: () => buildEndpoint(`${TAX_BASE}/input-tax-rules`),
  calculate: () => buildEndpoint(`${TAX_BASE}/calculate`),
  /** GET /api/v1/tax/pack?jurisdictionCode= — the validated engine pack (components, bases, places, supply types). ADR-0047. */
  pack: () => buildEndpoint(`${TAX_BASE}/pack`),
  /** GET /api/v1/tax/registration-number/parse?number= — format, check digit and the state a number encodes (derived state fields). */
  parseRegistrationNumber: () => buildEndpoint(`${TAX_BASE}/registration-number/parse`),
} as const;

export const taxProfileEndpoints = {
  partyProfile: (partyId: ID) => buildEndpointWithParams('/tax/party-profiles/:partyId', { partyId }),
  serviceMapping: (serviceId: ID) => buildEndpointWithParams('/tax/service-mappings/:serviceId', { serviceId }),
  /** GET — live items with no tax master under the entity's jurisdiction (ADR-0047 Phase 2 §7). */
  serviceMappingUnmappedCount: () => buildEndpoint('/tax/service-mappings/unmapped-count'),
  /** POST — pin every unmapped item to the entity's default tax master. */
  serviceMappingApplyDefault: () => buildEndpoint('/tax/service-mappings/apply-default'),
} as const;

/** /api/v1/tax/classifications — the classification master (HSN / SAC …) and the statutory units. ADR-0047 Phase 2 §7. */
const CLS_BASE = '/tax/classifications';
export const taxClassificationEndpoints = {
  list: () => buildEndpoint(CLS_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${CLS_BASE}/:id`, { id }),
  create: () => buildEndpoint(CLS_BASE),
  update: (id: ID) => buildEndpointWithParams(`${CLS_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${CLS_BASE}/:id`, { id }),
  units: () => buildEndpoint(`${CLS_BASE}/units`),
} as const;

const REG_BASE = '/tax/entity-registrations';
export const entityTaxRegistrationEndpoints = {
  list: () => buildEndpoint(REG_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${REG_BASE}/:id`, { id }),
  create: () => buildEndpoint(REG_BASE),
  update: (id: ID) => buildEndpointWithParams(`${REG_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${REG_BASE}/:id`, { id }),
} as const;

/** GET/PUT /api/v1/tax/entity-config — the entity's per-jurisdiction tax defaults (ADR-0047 Phase 2 §6). */
export const entityTaxConfigEndpoints = {
  get: () => buildEndpoint('/tax/entity-config'),
  upsert: () => buildEndpoint('/tax/entity-config'),
} as const;

/** The entity's registration status timeline (ADR-0048 §1 amended). */
export const taxRegistrationStatusEndpoints = {
  get: () => buildEndpoint('/tax/registration-status'),
  set: () => buildEndpoint('/tax/registration-status'),
  latest: () => buildEndpoint('/tax/registration-status/latest'),
} as const;

const ACC_BASE = '/tax/entity-accounts';
export const entityTaxAccountEndpoints = {
  list: () => buildEndpoint(ACC_BASE),
  getById: (id: ID) => buildEndpointWithParams(`${ACC_BASE}/:id`, { id }),
  create: () => buildEndpoint(ACC_BASE),
  update: (id: ID) => buildEndpointWithParams(`${ACC_BASE}/:id`, { id }),
  delete: (id: ID) => buildEndpointWithParams(`${ACC_BASE}/:id`, { id }),
} as const;
